<script>
/**
 * @component PatientList
 * @description Listado de pacientes con búsqueda, stats, filtros y acciones.
 *              Diseño card-based con avatares de iniciales y badges de estado.
 * @example <PatientList />
 */
import { mapGetters } from "vuex";
import pagination from "@/mixins/pagination";

import debounce from "debounce";
import helperService from "@/services/helper";
import patientService from "@/services/patient";
import userService from "@/services/user";
import inviteService from "@/services/invite";
import segmentService from "@/services/segment";
import {
  getMedicalInsuranceNumber,
  getMedicalInsurancePlan,
  getMedicalInsuranceName,
} from "@/services/patient-utils";
import { isProtocolsEnabled, isTasksEnabled } from "@/services/config";

import Spinner from "@/components/ui/Spinner";

const DEBOUNCE_MS_TIMEOUT = 500;

export default {
  name: "PatientList",

  mixins: [pagination],

  components: {
    Spinner,
  },

  data() {
    return {
      isProtocolsEnabled,
      isTasksEnabled,
      isLoading: false,
      isLoadingDoctors: false,
      isLoadingManagers: false,
      searchInput: "",
      patients: [],
      doctors: [],
      managers: [],
      doctorFilter: null,
      attendedLast30: "—",
      appointments7days: "—",
      appointments30days: "—",
    };
  },

  computed: {
    ...mapGetters(["isDoctor", "isManager", "user"]),
  },

  watch: {
    doctorFilter(newVal, oldVal) {
      if (newVal === oldVal) {
        return;
      }

      this.getPatients();
    },
  },

  created() {
    this.getPatients();

    if (!this.isDoctor) {
      this.getDoctors();
    }

    if (!this.isDoctor && !this.isManager) {
      this.getManagers();
    }

    this.$bus.$on("patient-update", (patient) => {
      const index = this.patients.findIndex((p) => p._id === patient._id);
      if (index >= 0) {
        this.$set(this.patients, index, patient);
      } else {
        this.patients.unshift(patient);
      }
    });
  },

  methods: {
    getDoctors() {
      this.isLoadingDoctors = true;

      userService
        .getDoctors()
        .then(({ docs }) => (this.doctors = docs))
        .finally(() => (this.isLoadingDoctors = false));
    },

    getManagers() {
      this.isLoadingManagers = true;

      userService
        .getManagers()
        .then(({ docs }) => (this.managers = docs))
        .finally(() => (this.isLoadingManagers = false));
    },

    /**
     * @description Calcula estadísticas locales del listado actual de pacientes
     */
    loadStats() {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      this.attendedLast30 = this.patients.filter((p) => {
        const lastEvent = p.patient?.lastEvent?.createdAt;
        return lastEvent && new Date(lastEvent) >= thirtyDaysAgo;
      }).length;

      // Turnos: placeholder, sin endpoint disponible por ahora
      this.appointments7days = "—";
      this.appointments30days = "—";
    },

    getPatients(query = {}) {
      this.isLoading = true;

      const defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        ...query,
      };

      defaultQuery.doctors = this.doctorFilter || undefined;

      patientService
        .get(defaultQuery)
        .then((res) => {
          this.patients = res.docs;
          this.pagination.total = res.total;
          this.loadStats();
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    debounceInput: debounce(function () {
      const query = {
        offset: 0,
        limit: this.pagination.limit,
      };

      const input = this.searchInput && this.searchInput.split(" ");
      // Una palabra → regex parcial (matchea fragmentos como "test" → "testing").
      // Antes usábamos $text $search que es match de palabra COMPLETA y dejaba
      // la lista sin filtrar para fragmentos — bug BUG-005 del reporte de QA.
      const useRegex = input && input.length === 1;
      // 2-3 palabras (nombre + apellido, dobles) → query de nombre dedicada.
      const useNameIndex = input && input.length > 1 && input.length <= 3;

      if (useRegex) {
        const term = this.searchInput.trim();
        // Escapar caracteres especiales del regex para que un punto, paréntesis,
        // etc. no rompan el match.
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const regex = { $regex: escaped, $options: "i" };
        query.$or = [
          { firstName: regex },
          { lastName: regex },
          { "governmentId.number": regex },
        ];
      } else if (useNameIndex) {
        Object.assign(query, helperService.createNameQuery(this.searchInput));
      }

      this.getPatients(query);
    }, DEBOUNCE_MS_TIMEOUT),

    getFullName(patient) {
      return helperService.getFullName(patient);
    },

    getMedicalInsuranceNumber,
    getMedicalInsurancePlan,
    getMedicalInsuranceName,

    getPatientDoctorsNames(doctors) {
      return helperService.getPatientDoctorsNames(doctors);
    },

    lockPatient(patient) {
      this.$confirm(
        `¿Desea bloquear a: ${helperService.getFullName(patient)}?`,
        "Bloquear",
        {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      )
        .then(() => {
          segmentService.track({ name: "Patient Locked" });
          userService.lock(patient._id).then(() => {
            const index = this.patients.findIndex((p) => p._id === patient._id);
            this.$set(this.patients, index, {
              ...this.patients[index],
              lockedAt: new Date(),
            });
          });
        })
        .catch(() => {});
    },

    unlockPatient(id) {
      segmentService.track({ name: "Patient Unlocked" });
      userService.unlock(id).then(() => {
        const index = this.patients.findIndex((p) => p.user._id === id);
        const modifiedPatient = { ...this.patients[index] };
        delete modifiedPatient.lockedAt;
        this.$set(this.patients, index, modifiedPatient);
      });
    },

    reSendInvite(username) {
      inviteService
        .sendInvite(username)
        .then(() =>
          this.$message.success(`Se reenvío una invitación a ${username}`)
        );
    },

    onPageChange() {
      this.getPatients();
    },

    goToPatientDashboard(id) {
      helperService.callIfNoSelectedText(() =>
        this.$router.push({
          name: "patient-dashboard",
          params: { id },
        })
      );
    },

    getPatientStatus(user) {
      const { patient } = user;

      if (patient?.status === "closed") {
        return "Cerrado";
      }

      const lastEvent = patient?.lastEvent;
      if (!lastEvent) {
        return "Paciente nuevo";
      }

      const lastEventWasClosedStatus = lastEvent.status === "closed";
      const isActive = patient.status === "active";

      // This happens when the patient has an active episode but the last event
      // was a closed event from another episode.
      if (isActive && lastEventWasClosedStatus) {
        return "Paciente nuevo";
      }

      const name = lastEvent && lastEvent.form && lastEvent.form.name;
      return name;
    },

    /**
     * @description Iniciales del paciente para el avatar
     * @param {Object} patient - Paciente
     * @returns {string}
     */
    getInitials(patient) {
      const first = (patient.firstName || "")[0] || "";
      const last = (patient.lastName || "")[0] || "";
      return (first + last).toUpperCase();
    },

    /**
     * @description Clase CSS del badge de estado
     * @param {Object} patient - Paciente
     * @returns {string}
     */
    getStatusClass(patient) {
      if (patient.lockedAt) return "badge--locked";
      if (patient.patient?.status === "closed") return "badge--closed";
      if (!patient.patient?.lastEvent) return "badge--new";
      return "badge--active";
    },

    /**
     * @description Label del badge de estado
     * @param {Object} patient - Paciente
     * @returns {string}
     */
    getStatusLabel(patient) {
      if (patient.lockedAt) return "Bloqueado";
      if (patient.patient?.status === "closed") return "Cerrado";
      if (!patient.patient?.lastEvent) return "Nuevo";
      return "Activo";
    },
  },
};
</script>

<template lang="pug">
section.patients-page
  //- Toolbar: nuevo paciente + buscador
  .page-toolbar
    router-link.btn-primary(
      v-auth="'patient.create'"
      :to="{ name: 'create-patient' }"
    )
      micon(name="add" width="16" height="16")
      | Nuevo Paciente
    el-input.search-input(
      prefix-icon="el-icon-search"
      v-model="searchInput"
      @input="debounceInput"
      placeholder="Buscar por nombre, documento..."
      clearable
      size="small"
    )

  //- Table
  .table-card
    spinner(v-if="isLoading")
    table.data-table(v-else)
      thead
        tr
          th Paciente
          th Documento
          th Seguro Salud
          th Plan
          th Nro Afiliado
          th Edad
          th Última Atención
          th
      tbody
        tr.data-table__row(
          v-for="patient in patients"
          :key="patient._id"
          @click="goToPatientDashboard(patient._id)"
        )
          td
            .patient-name
              .patient-avatar {{ getInitials(patient) }}
              .patient-name__full(:title="getFullName(patient)") {{ getFullName(patient) }}
          td {{ patient.governmentId?.number || '—' }}
          td {{ getMedicalInsuranceName(patient) || '—' }}
          td {{ getMedicalInsurancePlan(patient) || '—' }}
          td {{ getMedicalInsuranceNumber(patient) || '—' }}
          td {{ patient.bornAt | age }}
          td {{ patient.patient?.lastEvent?.createdAt | formatDate }}
          td.actions-cell
            .actions-container
              el-dropdown(trigger="click")
                .button.button--action.el-dropdown-link(@click.stop)
                  micon(name="more_horiz")
                el-dropdown-menu(slot="dropdown")
                  el-dropdown-item(v-auth="'patient.update'" v-if="!isDoctor")
                    router-link(:to="{ name: 'update-patient', params: { id: patient._id }}")
                      micon(name="edit")
                      | Editar paciente
                  el-dropdown-item(v-auth="'treatment.assign'" v-if="isProtocolsEnabled && !isDoctor")
                    router-link(:to="{ name: 'assign-treatment-patient', params: { id: patient._id }}")
                      micon(name="note_add")
                      | Asignar Protocolo
                  el-dropdown-item(v-auth="'patient.access-private'" v-if="isTasksEnabled && !isDoctor")
                    router-link(:to="{ name: 'patient-tasks', params: { id: patient._id }}")
                      micon(name="calendar")
                      | Ver tareas
                  el-dropdown-item(v-show="!patient.lockedAt" v-auth="'patient.update'")
                    div(@click="lockPatient(patient)")
                      micon(name="lock")
                      | Bloquear paciente
                  el-dropdown-item(v-show="patient.lockedAt" v-auth="'patient.update'")
                    div(@click="unlockPatient(patient._id)")
                      micon(name="lock_open")
                      | Desbloquear paciente
                  el-dropdown-item
                    div(@click="reSendInvite(patient.username)")
                      micon(name="email")
                      | Reenviar invitación

    pagination(
      :isLoading="isLoading"
      :limit="pagination.limit"
      :total="pagination.total"
      @pagination="setPagination"
    )
</template>

<style lang="scss" scoped>
.patients-page {
  padding: 0;
}

/* =========================================
   Page Toolbar
   ========================================= */
.page-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.search-input {
  width: 280px;

  ::v-deep .el-input__inner {
    border-radius: 8px;
    border: 1px solid $border-color;
    font-size: 13px;

    &:focus {
      border-color: $teal;
      box-shadow: 0 0 0 3px rgba(5, 229, 186, 0.1);
    }
  }
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: $navy;
  color: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s;
  white-space: nowrap;

  .micon {
    fill: #fff;
  }

  &:hover {
    background: darken(#002d5e, 8%);
    color: #fff;
  }
}

/* =========================================
   Stats Row
   ========================================= */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card {
  background: #fff;
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 10px 14px;
  text-align: center;

  &__value {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
  }

  &__label {
    font-size: 10px;
    font-weight: 500;
    color: $text-secondary-new;
    margin-top: 2px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &--success {
    border-left: 3px solid $teal;

    .stat-card__value {
      color: #065f46;
    }
  }

  &--info {
    border-left: 3px solid #3b82f6;

    .stat-card__value {
      color: #1e40af;
    }
  }

  &--purple {
    border-left: 3px solid #8b5cf6;

    .stat-card__value {
      color: #6d28d9;
    }
  }
}

/* =========================================
   Table Card
   ========================================= */
.table-card {
  background: #fff;
  border: 1px solid $border-color;
  border-radius: 12px;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;

  thead th {
    position: static;
    box-shadow: none;
    background: $header-bg;
    font-size: 11px;
    font-weight: 600;
    color: $text-secondary-new;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 12px 16px;
    text-align: left;
    border-bottom: 1px solid $border-color;

    &::after {
      display: none;
    }
  }

  &__row {
    cursor: pointer;
    transition: background 0.15s;
    border-bottom: 1px solid #f1f5f9;

    &:hover {
      background: #e8fdf8;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  tbody td {
    padding: 12px 16px;
    font-size: 13px;
    color: $text-primary-new;
    height: auto;
  }
}

/* =========================================
   Patient Name + Avatar
   ========================================= */
.patient-name {
  display: flex;
  align-items: center;
  gap: 10px;
  // BUG-012: nombres largos rompían la grilla empujando las demás columnas.
  // Limitamos el ancho y truncamos con ellipsis (el full name se ve en hover).
  min-width: 0;
  max-width: 260px;

  &__full {
    font-weight: 600;
    color: $navy;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__username {
    font-size: 11px;
    color: $text-secondary-new;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.patient-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: $teal-light;
  color: $navy;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

/* =========================================
   Status Badges
   ========================================= */
.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;

  &--active {
    background: #e8fdf8;
    color: #065f46;
  }

  &--locked {
    background: #fef3c7;
    color: #92400e;
  }

  &--new {
    background: #dbeafe;
    color: #1e40af;
  }

  &--closed {
    background: #f1f5f9;
    color: #64748b;
  }
}

/* =========================================
   Actions
   ========================================= */
.actions-cell {
  width: 50px;
  text-align: center;
}
</style>
